<template>
  <div class="wallet">
    <!-- Balance Cards Section -->
    <h2 class="section-title">{{ $t('wallet.your_balances') }}</h2>

    <!-- Balance Loading -->
    <div v-if="store.loadingBalance" class="balance-grid">
      <div v-for="i in 2" :key="i" class="balance-card-skeleton">
        <div class="bcs-label"></div>
        <div class="bcs-amount"></div>
      </div>
    </div>

    <!-- Balance Error -->
    <div v-else-if="store.balanceError" class="wallet-error">
      <p>{{ store.balanceError }}</p>
      <button class="btn-cta" @click="store.fetchBalance()">{{ $t('wallet.try_again') }}</button>
    </div>

    <!-- Balance Cards -->
    <div v-else-if="store.balance" class="balance-grid">
      <div class="balance-card cash">
        <div class="balance-header">
          <div>
            <div class="balance-label">{{ $t('wallet.cash_balance') }}</div>
            <div class="balance-currency">{{ store.balance.currency }}</div>
          </div>
          <div class="balance-icon cash">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
        </div>
        <div class="balance-amount">{{ formatAmount(store.balance.balance) }}</div>
      </div>

      <div class="balance-card bonus">
        <div class="balance-header">
          <div>
            <div class="balance-label">{{ $t('wallet.bonus_balance') }}</div>
            <div class="balance-currency">{{ store.balance.currency }}</div>
          </div>
          <div class="balance-icon bonus">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
            </svg>
          </div>
        </div>
        <div class="balance-amount bonus-text">{{ formatAmount(store.balance.bonus_balance) }}</div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="wallet-actions">
      <button class="btn-action deposit" @click="navigateToDeposit">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        {{ $t('wallet.deposit') }}
      </button>
      <button class="btn-action withdraw" @click="navigateToWithdraw">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        {{ $t('wallet.withdraw') }}
      </button>
    </div>

    <!-- Transaction History Section -->
    <div class="transactions-section">
      <h2 class="section-title">{{ $t('wallet.transaction_history') }}</h2>

      <!-- Filter Tabs -->
      <div class="tx-controls">
        <div class="tx-tabs">
          <button
            v-for="tab in filterTabs"
            :key="tab.value"
            class="tx-tab"
            :class="{ active: store.activeFilter === tab.value }"
            @click="store.setFilter(tab.value)"
          >
            {{ $t(tab.label) }}
          </button>
        </div>

        <div class="tx-date-filters">
          <input
            v-model="fromDate"
            type="date"
            class="date-input"
            :placeholder="$t('wallet.date_from')"
            @change="onDateChange"
          >
          <input
            v-model="toDate"
            type="date"
            class="date-input"
            :placeholder="$t('wallet.date_to')"
            @change="onDateChange"
          >
        </div>
      </div>

      <!-- Transactions Loading -->
      <div v-if="store.loadingTransactions" class="tx-table-wrap">
        <div class="tx-table-header">
          <span>{{ $t('wallet.col_date') }}</span>
          <span>{{ $t('wallet.col_type') }}</span>
          <span>{{ $t('wallet.col_amount') }}</span>
          <span>{{ $t('wallet.col_reason') }}</span>
        </div>
        <div v-for="i in 5" :key="i" class="tx-row-skeleton">
          <div class="tx-skel-cell wide"></div>
          <div class="tx-skel-cell"></div>
          <div class="tx-skel-cell"></div>
          <div class="tx-skel-cell wide"></div>
        </div>
      </div>

      <!-- Transactions Error -->
      <div v-else-if="store.transactionsError" class="wallet-error">
        <p>{{ store.transactionsError }}</p>
        <button class="btn-cta" @click="store.fetchTransactions(1)">{{ $t('wallet.try_again') }}</button>
      </div>

      <!-- Empty Transactions -->
      <div v-else-if="store.transactions.length === 0" class="tx-empty">
        <p>{{ $t('wallet.no_transactions') }}</p>
        <p class="tx-empty-sub">{{ $t('wallet.no_transactions_sub') }}</p>
      </div>

      <!-- Transaction Table -->
      <div v-else>
        <div class="tx-table-wrap">
          <div class="tx-table-header">
            <span>{{ $t('wallet.col_date') }}</span>
            <span>{{ $t('wallet.col_type') }}</span>
            <span>{{ $t('wallet.col_amount') }}</span>
            <span>{{ $t('wallet.col_reason') }}</span>
          </div>
          <div
            v-for="tx in store.transactions"
            :key="tx.id"
            class="tx-row"
          >
            <span class="tx-date">{{ formatDate(tx.created_at) }}</span>
            <span>
              <span class="tx-type-badge" :class="tx.type">
                {{ tx.type === 'credit' ? $t('wallet.credit_badge') : $t('wallet.debit_badge') }}
              </span>
            </span>
            <span class="tx-amount" :class="tx.type === 'credit' ? 'positive' : 'negative'">
              {{ tx.type === 'credit' ? '+' : '-' }}{{ formatAmount(Math.abs(tx.amount), tx.currency) }}
            </span>
            <span class="tx-reason">{{ tx.notes || '-' }}</span>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="store.transactionsMeta.last_page > 1" class="tx-pagination">
          <button
            class="btn-page"
            :disabled="store.transactionsMeta.current_page <= 1"
            @click="store.goToPage(store.transactionsMeta.current_page - 1)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m15 18-6-6 6-6"/>
            </svg>
          </button>
          <span class="page-info">
            {{ $t('common.page_of', { current: store.transactionsMeta.current_page, total: store.transactionsMeta.last_page }) }}
          </span>
          <button
            class="btn-page"
            :disabled="store.transactionsMeta.current_page >= store.transactionsMeta.last_page"
            @click="store.goToPage(store.transactionsMeta.current_page + 1)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useWalletStore } from '@/stores/wallet'
import type { TransactionFilter } from '@/stores/wallet'
import { formatCurrency } from '@/utils/formatCurrency'

const router = useRouter()
const store = useWalletStore()

const fromDate = ref('')
const toDate = ref('')

const filterTabs: { label: string; value: TransactionFilter }[] = [
  { label: 'wallet.filter_all', value: 'all' },
  { label: 'wallet.filter_credits', value: 'credit' },
  { label: 'wallet.filter_debits', value: 'debit' },
]

function formatAmount(amount: number, currency?: string): string {
  return formatCurrency(Math.round(amount * 100), currency ?? store.balance?.currency ?? 'USD')
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function onDateChange() {
  store.setDateRange(fromDate.value, toDate.value)
}

function navigateToDeposit() {
  router.push('/player/deposit')
}

function navigateToWithdraw() {
  router.push('/player/withdraw')
}

onMounted(() => {
  store.fetchBalance()
  store.fetchTransactions(1)
})
</script>

<style scoped>
.wallet {
  padding: var(--space-6, 24px);
  max-width: 900px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Section Title */
.section-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Balance Cards */
.balance-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.balance-card {
  padding: 24px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  border-left: 4px solid;
  transition: all var(--transition-fast, 150ms ease);
}

.balance-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
}

.balance-card.cash {
  border-left-color: var(--accent-primary, #00e701);
}

.balance-card.bonus {
  border-left-color: var(--accent-gold, #ffd700);
}

.balance-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.balance-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 2px;
}

.balance-currency {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.balance-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.balance-icon.cash {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
}

.balance-icon.bonus {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
}

.balance-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.balance-amount.bonus-text {
  color: var(--accent-gold, #ffd700);
}

/* Action Buttons */
.wallet-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
}

.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 24px;
  font-size: 14px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-action.deposit {
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
}

.btn-action.deposit:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

.btn-action.withdraw {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-action.withdraw:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

/* Transactions Section */
.transactions-section {
  margin-bottom: 32px;
}

/* Controls Row */
.tx-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.tx-tabs {
  display: flex;
  gap: 4px;
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  padding: 3px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.tx-tab {
  padding: 7px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.tx-tab.active {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

.tx-tab:hover:not(.active) {
  color: var(--text-primary, #fff);
}

.tx-date-filters {
  display: flex;
  gap: 8px;
}

.date-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  padding: 7px 10px;
  color: var(--text-primary, #fff);
  font-size: 12px;
  font-family: inherit;
  transition: border-color var(--transition-fast, 150ms ease);
}

.date-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

/* Transaction Table */
.tx-table-wrap {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.tx-table-header {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 1fr 1.5fr;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.tx-row {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 1fr 1.5fr;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  align-items: center;
  font-size: 13px;
  transition: background var(--transition-fast, 150ms ease);
}

.tx-row:last-child {
  border-bottom: none;
}

.tx-row:hover {
  background: rgba(0, 231, 1, 0.02);
}

.tx-date {
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
}

.tx-type-badge {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: var(--radius-sm, 4px);
  font-size: 11px;
  font-weight: 600;
}

.tx-type-badge.credit {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.tx-type-badge.debit {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.tx-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
}

.tx-amount.positive {
  color: var(--color-success, #00e701);
}

.tx-amount.negative {
  color: var(--color-danger, #ed4245);
}

.tx-reason {
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Pagination */
.tx-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0 8px;
}

.btn-page {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-page:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-page:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
}

/* Error State */
.wallet-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-cta {
  padding: 11px 22px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-cta:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

/* Empty state */
.tx-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 16px;
  gap: 8px;
}

.tx-empty-sub {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Skeleton Loading */
.balance-card-skeleton {
  padding: 24px;
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.8) 0%, rgba(33, 55, 67, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg, 12px);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bcs-label {
  height: 14px;
  width: 50%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.bcs-amount {
  height: 24px;
  width: 70%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.tx-row-skeleton {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 1fr 1.5fr;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.tx-skel-cell {
  height: 14px;
  width: 60%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.tx-skel-cell.wide {
  width: 80%;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Responsive */
@media (max-width: 768px) {
  .wallet {
    padding: var(--space-4, 16px);
  }

  .balance-grid {
    grid-template-columns: 1fr;
  }

  .wallet-actions {
    flex-direction: column;
  }

  .tx-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .tx-date-filters {
    width: 100%;
  }

  .date-input {
    flex: 1;
  }

  .tx-table-header,
  .tx-row,
  .tx-row-skeleton {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .tx-table-header span:nth-child(4),
  .tx-row .tx-reason {
    display: none;
  }
}

/* RTL Support */
/* Balance card: move colored accent border to the right side */
[dir='rtl'] .balance-card {
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-right: 4px solid transparent;
}

[dir='rtl'] .balance-card.cash {
  border-right-color: var(--accent-primary, #00e701);
}

[dir='rtl'] .balance-card.bonus {
  border-right-color: var(--accent-gold, #ffd700);
}

/* Monetary amounts: always LTR so +/- and digits read correctly */
[dir='rtl'] .balance-amount,
[dir='rtl'] .tx-amount {
  direction: ltr;
  unicode-bidi: embed;
  display: inline-block;
  text-align: right;
}
</style>
